const COLLECTION_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';

export function normalizeCollectionId(value: string): string {
  const code = [...value.toUpperCase()]
    .filter(character => COLLECTION_ALPHABET.includes(character))
    .slice(0, 8)
    .join('');
  return code.length > 4 ? `${code.slice(0, 4)}-${code.slice(4)}` : code;
}

export function isCompleteCollectionId(value: string): boolean {
  return /^[ABCDEFGHJKMNPQRSTUVWXYZ23456789]{4}-[ABCDEFGHJKMNPQRSTUVWXYZ23456789]{4}$/.test(value);
}

export function createValidationGuard() {
  let version = 0;
  return {
    begin() {
      const requestVersion = ++version;
      return () => requestVersion === version;
    },
    invalidate() {
      version += 1;
    },
  };
}

export interface CollectionValidationData {
  id: string;
  staffName: string;
  staffEmail: string;
  requireCui?: boolean;
  expiresAt: string | null;
  notes?: string | null;
}

export function collectionUploadHandoff(collectionId: string, data: CollectionValidationData) {
  return {
    pathname: `/external/upload/${encodeURIComponent(collectionId)}`,
    collectionData: {
      collectionId,
      staffName: data.staffName,
      staffEmail: data.staffEmail,
      requireCui: data.requireCui ?? false,
      expiresAt: data.expiresAt,
      notes: data.notes ?? null,
    },
  };
}

export function signInPath(pathname: string, search = '', hash = ''): string {
  if (pathname === '/signin') return `${pathname}${search}${hash}`;
  return `/signin?returnTo=${encodeURIComponent(pathname + search + hash)}`;
}

export function protectedRouteDecision(isAuthenticated: boolean, isAdmin: boolean, requireAdmin: boolean) {
  if (!isAuthenticated) return 'signin';
  return requireAdmin && !isAdmin ? 'denied' : 'allowed';
}

export const primaryNavigation = [
  { to: '/', label: 'Home' },
  { to: '/send', label: 'Send Files' },
  { to: '/receive', label: 'Request Files' },
  { to: '/files', label: 'Transfers' },
  { to: '/activity', label: 'Activity' },
  { to: '/approvals', label: 'Approvals' },
] as const;

export function isNavigationActive(destination: string, pathname: string): boolean {
  if (destination === '/send' && pathname === '/upload') return true;
  if (destination === '/receive' && pathname === '/collections/new') return true;
  if (destination === '/files' && pathname.startsWith('/collections/') && pathname !== '/collections/new') return true;
  return pathname === destination;
}

export function dashboardQueryState(queries: readonly { isError: boolean; isPending: boolean }[]) {
  if (queries.some(query => query.isError)) return 'error';
  if (queries.some(query => query.isPending)) return 'loading';
  return 'ready';
}